<template>
  <div>
    <div class="p-6 pb-0">
      <BreadCrumbs :navigation="navigation" />
    </div>

    <div class="p-6">
      <h1 class="text-2xl font-semibold mb-1">{{ $t('rp.import.title') }}</h1>
      <p class="text-gray-500 mb-6">{{ registry.name }}</p>

      <!-- Stepper -->
      <div class="flex items-center mb-8">
        <template v-for="(label, index) in stepLabels" :key="index">
          <div class="flex items-center">
            <div
              class="w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold"
              :class="step >= index + 1 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'"
            >
              {{ index + 1 }}
            </div>
            <span class="ml-2 text-sm" :class="step >= index + 1 ? 'text-blue-600 font-medium' : 'text-gray-500'">
              {{ label }}
            </span>
          </div>
          <div v-if="index < stepLabels.length - 1" class="flex-1 h-px bg-gray-300 mx-3"></div>
        </template>
      </div>

      <!-- STEP 1 - Upload -->
      <div v-show="step === 1">
        <div class="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
          <input
            ref="fileInput"
            type="file"
            accept=".xlsx,.csv"
            class="hidden"
            @change="onFileSelected"
          />
          <v-icon name="ri-file-excel2-line" scale="2.5" class="text-gray-400 mb-3" />
          <p class="text-gray-600 mb-4">{{ $t('rp.import.upload.dropHint') }}</p>
          <button
            type="button"
            class="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md"
            @click="$refs.fileInput.click()"
          >
            {{ $t('rp.import.upload.selectFile') }}
          </button>
          <p class="text-xs text-gray-400 mt-3">.xlsx, .csv — max {{ maxFileSizeMb }} MB</p>
        </div>

        <div v-if="uploadError" class="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {{ uploadError }}
        </div>

        <div v-if="fileName" class="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div class="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div><span class="text-gray-500">{{ $t('rp.import.upload.fileName') }}:</span> <strong>{{ fileName }}</strong></div>
            <div><span class="text-gray-500">{{ $t('rp.import.upload.fileSize') }}:</span> <strong>{{ formattedSize }}</strong></div>
            <div><span class="text-gray-500">{{ $t('rp.import.upload.rowsCount') }}:</span> <strong>{{ rows.length }}</strong></div>
            <div><span class="text-gray-500">{{ $t('rp.import.upload.headers') }}:</span> <strong>{{ headers.length }}</strong></div>
          </div>

          <div v-if="headers.length" class="mt-4 overflow-x-auto">
            <p class="text-sm font-medium mb-2">{{ $t('rp.import.upload.preview') }}</p>
            <table class="min-w-full text-xs border">
              <thead class="bg-gray-100">
                <tr>
                  <th v-for="h in headers" :key="h" class="border px-2 py-1 text-left">{{ h }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, i) in previewRows" :key="i">
                  <td v-for="h in headers" :key="h" class="border px-2 py-1">{{ row[h] }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="mt-6 flex justify-between">
          <Link :href="backHref" class="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50">
            {{ $t('rp.import.confirm.backToRegistry') }}
          </Link>
          <button
            type="button"
            :disabled="!canProceedUpload"
            class="bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white px-4 py-2 rounded-md"
            @click="goToMapping"
          >
            {{ $t('rp.import.common.next') }}
          </button>
        </div>
      </div>

      <!-- STEP 2 - Mapping -->
      <div v-show="step === 2">
        <div class="overflow-x-auto">
          <table class="min-w-full text-sm border">
            <thead class="bg-gray-100">
              <tr>
                <th class="border px-3 py-2 text-left">{{ $t('rp.import.mapping.field') }}</th>
                <th class="border px-3 py-2 text-left">{{ $t('rp.import.mapping.type') }}</th>
                <th class="border px-3 py-2 text-center">{{ $t('rp.import.mapping.required') }}</th>
                <th class="border px-3 py-2 text-left">{{ $t('rp.import.mapping.sourceColumn') }}</th>
                <th class="border px-3 py-2 text-left">{{ $t('rp.import.mapping.interpretation') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="entry in mapping" :key="entry.column_id" :class="isRequiredNotMapped(entry) ? 'bg-red-50' : ''">
                <td class="border px-3 py-2 font-medium">{{ columnName(entry.column_id) }}</td>
                <td class="border px-3 py-2">
                  <span class="inline-block bg-gray-200 text-gray-700 rounded px-2 py-0.5 text-xs">
                    {{ $t('rp.import.types.' + columnType(entry.column_id)) }}
                  </span>
                </td>
                <td class="border px-3 py-2 text-center">
                  <span v-if="columnRequired(entry.column_id)" class="text-red-600 font-bold">*</span>
                </td>
                <td class="border px-3 py-2">
                  <select v-model="entry.source_column" class="border rounded px-2 py-1 w-full">
                    <option :value="null">{{ columnRequired(entry.column_id) ? '— ' + $t('rp.import.mapping.selectColumn') + ' —' : $t('rp.import.mapping.doNotImport') }}</option>
                    <option v-for="h in headers" :key="h" :value="h">{{ h }}</option>
                  </select>
                  <p v-if="isRequiredNotMapped(entry)" class="text-xs text-red-600 mt-1">
                    {{ $t('rp.import.mapping.requiredNotMapped') }}
                  </p>
                </td>
                <td class="border px-3 py-2">
                  <!-- number -->
                  <div v-if="columnType(entry.column_id) === 'number'" class="flex flex-col gap-1">
                    <label class="text-xs text-gray-500">{{ $t('rp.import.mapping.decimalSeparator') }}</label>
                    <select v-model="entry.decimal_separator" class="border rounded px-2 py-1">
                      <option v-for="d in config.decimal_separators" :key="d" :value="d">{{ d }}</option>
                    </select>
                    <label class="text-xs text-gray-500 mt-1">{{ $t('rp.import.mapping.thousandsSeparator') }}</label>
                    <select v-model="entry.thousands_separator" class="border rounded px-2 py-1">
                      <option v-for="t in config.thousands_separators" :key="t" :value="t">{{ t === '' ? $t('rp.import.common.thousandsNone') : t }}</option>
                    </select>
                  </div>
                  <!-- date -->
                  <div v-else-if="columnType(entry.column_id) === 'date'" class="flex flex-col gap-1">
                    <label class="text-xs text-gray-500">{{ $t('rp.import.mapping.dateFormat') }}</label>
                    <select v-model="entry.date_format" class="border rounded px-2 py-1">
                      <option v-for="f in config.date_formats" :key="f" :value="f">{{ f }}</option>
                    </select>
                  </div>
                  <!-- select -->
                  <div v-else-if="columnType(entry.column_id) === 'select'" class="flex flex-col gap-1">
                    <span class="text-xs text-gray-500">{{ $t('rp.import.mapping.allowedOptions') }}:</span>
                    <div class="flex flex-wrap gap-1">
                      <span v-for="opt in columnOptions(entry.column_id)" :key="opt.value" class="bg-gray-100 border rounded px-1.5 py-0.5 text-xs">{{ opt.label }}</span>
                    </div>
                    <label v-if="isBooleanLike(entry.column_id)" class="flex items-center gap-1 text-xs mt-1">
                      <input type="checkbox" v-model="entry.recognize_boolean" />
                      {{ $t('rp.import.mapping.recognizeBoolean') }}
                    </label>
                  </div>
                  <!-- query -->
                  <div v-else-if="isQueryType(entry.column_id)" class="flex flex-col gap-1">
                    <label class="text-xs text-gray-500">{{ $t('rp.import.mapping.lookupMode') }}</label>
                    <select v-model="entry.lookup_mode" class="border rounded px-2 py-1">
                      <option v-for="m in config.lookup_modes" :key="m" :value="m">{{ $t('rp.import.lookupModes.' + m) }}</option>
                    </select>
                  </div>
                  <span v-else class="text-xs text-gray-400">—</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="mt-6 flex justify-between">
          <button type="button" class="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50" @click="step = 1">
            {{ $t('rp.import.common.back') }}
          </button>
          <button
            type="button"
            :disabled="!canProceedMapping"
            class="bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white px-4 py-2 rounded-md"
            @click="runValidation"
          >
            {{ $t('rp.import.common.next') }}
          </button>
        </div>
      </div>

      <!-- STEP 3 - Validation -->
      <div v-show="step === 3">
        <div v-if="validating" class="text-center py-10 text-gray-500">
          {{ $t('rp.import.validation.running') }}
        </div>

        <div v-else-if="report">
          <div class="grid grid-cols-3 gap-4 mb-6">
            <div class="bg-gray-50 border rounded-lg p-4 text-center">
              <div class="text-2xl font-semibold">{{ report.summary.total_rows }}</div>
              <div class="text-xs text-gray-500">{{ $t('rp.import.validation.total') }}</div>
            </div>
            <div class="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
              <div class="text-2xl font-semibold text-green-700">{{ report.summary.valid_rows }}</div>
              <div class="text-xs text-gray-500">{{ $t('rp.import.validation.valid') }}</div>
            </div>
            <div class="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
              <div class="text-2xl font-semibold text-red-700">{{ report.summary.invalid_rows }}</div>
              <div class="text-xs text-gray-500">{{ $t('rp.import.validation.invalid') }}</div>
            </div>
          </div>

          <div v-if="errorList.length" class="overflow-x-auto">
            <p class="text-sm font-medium mb-2">{{ $t('rp.import.validation.errorsTable') }}</p>
            <table class="min-w-full text-xs border">
              <thead class="bg-gray-100">
                <tr>
                  <th class="border px-2 py-1 text-left">{{ $t('rp.import.validation.rowNumber') }}</th>
                  <th class="border px-2 py-1 text-left">{{ $t('rp.import.validation.field') }}</th>
                  <th class="border px-2 py-1 text-left">{{ $t('rp.import.validation.sourceColumn') }}</th>
                  <th class="border px-2 py-1 text-left">{{ $t('rp.import.validation.originalValue') }}</th>
                  <th class="border px-2 py-1 text-left">{{ $t('rp.import.validation.message') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(err, i) in errorList" :key="i">
                  <td class="border px-2 py-1">{{ err.row_number }}</td>
                  <td class="border px-2 py-1">{{ err.field }}</td>
                  <td class="border px-2 py-1">{{ err.source_column }}</td>
                  <td class="border px-2 py-1">{{ err.original_value }}</td>
                  <td class="border px-2 py-1 text-red-700">{{ err.message }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-else class="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
            {{ $t('rp.import.validation.noErrors') }}
          </div>

          <div v-if="!report.valid" class="mt-4 bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded">
            {{ $t('rp.import.validation.blocked') }}
          </div>
        </div>

        <div class="mt-6 flex justify-between">
          <button type="button" class="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50" @click="step = 2">
            {{ $t('rp.import.common.back') }}
          </button>
          <button
            type="button"
            :disabled="!canProceedValidation"
            class="bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white px-4 py-2 rounded-md"
            @click="step = 4"
          >
            {{ $t('rp.import.common.next') }}
          </button>
        </div>
      </div>

      <!-- STEP 4 - Confirm -->
      <div v-show="step === 4">
        <div v-if="!committed" class="bg-gray-50 border rounded-lg p-6">
          <dl class="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div><dt class="text-gray-500 inline">{{ $t('rp.import.confirm.registry') }}:</dt> <dd class="inline font-medium">{{ registry.name }}</dd></div>
            <div><dt class="text-gray-500 inline">{{ $t('rp.import.confirm.file') }}:</dt> <dd class="inline font-medium">{{ fileName }}</dd></div>
            <div><dt class="text-gray-500 inline">{{ $t('rp.import.confirm.rowsToImport') }}:</dt> <dd class="inline font-medium">{{ report ? report.summary.valid_rows : 0 }}</dd></div>
          </dl>

          <div class="mt-4 bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded text-sm">
            {{ $t('rp.import.confirm.atomicWarning') }}
          </div>

          <div v-if="commitError" class="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
            {{ commitError }}
          </div>

          <div class="mt-6 flex justify-between">
            <button type="button" class="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50" @click="step = 3">
              {{ $t('rp.import.common.back') }}
            </button>
            <button
              type="button"
              :disabled="committing || !canProceedValidation"
              class="bg-green-600 hover:bg-green-700 disabled:opacity-40 text-white px-4 py-2 rounded-md"
              @click="commit"
            >
              {{ committing ? $t('rp.import.confirm.importing') : $t('rp.import.confirm.import') }}
            </button>
          </div>
        </div>

        <div v-else class="text-center py-10">
          <v-icon name="hi-solid-check-circle" scale="3" class="text-green-600 mb-4" />
          <p class="text-lg font-medium mb-6">{{ $t('rp.import.confirm.success', { count: createdCount }) }}</p>
          <Link :href="backHref" class="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md">
            {{ $t('rp.import.confirm.backToRegistry') }}
          </Link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { Link } from '@inertiajs/vue3';
import BreadCrumbs from '@/Components/BreadCrumbs.vue';
import {
  parseSpreadsheetBuffer,
  getExtension,
  MAX_EXCEL_FILE_SIZE,
  MAX_EXCEL_FILE_SIZE_MB,
  ACCEPTED_EXTENSIONS,
} from '@/utils/excelParser.js';

export default {
  components: { Link, BreadCrumbs },
  props: {
    registry: { type: Object, required: true },
    columns: { type: Array, default: () => [] },
    config: { type: Object, required: true },
  },
  data() {
    return {
      step: 1,
      fileName: null,
      fileSize: 0,
      headers: [],
      rows: [],
      mapping: [],
      validating: false,
      report: null,
      committing: false,
      committed: false,
      createdCount: 0,
      uploadError: null,
      commitError: null,
      navigation: [
        { name: 'Dashboard', href: route('dashboard') },
        { name: this.$t('rp.import.title'), href: '#' },
        { name: this.registry?.name || '', href: '#' },
      ],
    };
  },
  created() {
    this.mapping = this.columns.map((c) => ({
      column_id: c.id,
      source_column: null,
      lookup_mode: this.config.default_lookup_mode,
      date_format: this.config.date_formats[0],
      decimal_separator: '.',
      thousands_separator: '',
      recognize_boolean: false,
    }));
  },
  computed: {
    stepLabels() {
      return [
        this.$t('rp.import.steps.upload'),
        this.$t('rp.import.steps.mapping'),
        this.$t('rp.import.steps.validation'),
        this.$t('rp.import.steps.confirm'),
      ];
    },
    maxFileSizeMb() {
      return MAX_EXCEL_FILE_SIZE_MB;
    },
    formattedSize() {
      if (this.fileSize < 1024) return `${this.fileSize} B`;
      if (this.fileSize < 1024 * 1024) return `${(this.fileSize / 1024).toFixed(1)} KB`;
      return `${(this.fileSize / (1024 * 1024)).toFixed(2)} MB`;
    },
    previewRows() {
      return this.rows.slice(0, 10);
    },
    backHref() {
      return route('modules.registri-personalizzati.show', this.registry.id);
    },
    canProceedUpload() {
      return this.headers.length > 0 && this.rows.length > 0;
    },
    canProceedMapping() {
      return !this.mapping.some((e) => this.columnRequired(e.column_id) && !e.source_column);
    },
    canProceedValidation() {
      return !!this.report && this.report.valid;
    },
    errorList() {
      if (!this.report) return [];
      const list = [];
      for (const row of this.report.rows) {
        for (const err of row.errors) {
          list.push({ row_number: row.row_number, ...err });
        }
      }
      return list;
    },
  },
  methods: {
    columnMeta(id) {
      return this.columns.find((c) => c.id === id) || {};
    },
    columnName(id) {
      return this.columnMeta(id).name;
    },
    columnType(id) {
      return this.columnMeta(id).type;
    },
    columnRequired(id) {
      return !!this.columnMeta(id).is_required;
    },
    columnOptions(id) {
      return this.columnMeta(id).options || [];
    },
    isQueryType(id) {
      const t = this.columnType(id);
      return t === 'magoquery' || t === 'portalquery';
    },
    isBooleanLike(id) {
      const opts = this.columnOptions(id);
      if (opts.length !== 2) return false;
      const positive = ['si', 'sì', 'yes', 'y', 'true', '1', 'x', 'presente', 'attivo'];
      const negative = ['no', 'n', 'false', '0', 'assente', 'non attivo'];
      const norm = opts.map((o) => String(o.value).trim().toLowerCase());
      return norm.some((v) => positive.includes(v)) && norm.some((v) => negative.includes(v));
    },
    isRequiredNotMapped(entry) {
      return this.columnRequired(entry.column_id) && !entry.source_column;
    },
    async onFileSelected(event) {
      this.uploadError = null;
      const file = event.target.files && event.target.files[0];
      if (!file) return;

      const ext = getExtension(file.name);
      if (!ACCEPTED_EXTENSIONS.includes(ext)) {
        this.uploadError = this.$t('rp.import.upload.invalidExtension');
        return;
      }
      if (file.size > MAX_EXCEL_FILE_SIZE) {
        this.uploadError = this.$t('rp.import.upload.tooLarge', { max: this.maxFileSizeMb });
        return;
      }

      try {
        const buffer = await file.arrayBuffer();
        const parsed = await parseSpreadsheetBuffer(buffer, ext);
        if (!parsed || parsed.length === 0) {
          this.uploadError = this.$t('rp.import.upload.empty');
          return;
        }
        this.rows = parsed;
        this.headers = Object.keys(parsed[0]);
        this.fileName = file.name;
        this.fileSize = file.size;
        this.report = null;
        this.autoMap();
      } catch (e) {
        this.uploadError = this.$t('rp.import.errors.generic');
      }
    },
    autoMap() {
      const normalized = this.headers.map((h) => ({ raw: h, norm: h.trim().toLowerCase() }));
      for (const entry of this.mapping) {
        const name = (this.columnName(entry.column_id) || '').trim().toLowerCase();
        const match = normalized.find((h) => h.norm === name);
        entry.source_column = match ? match.raw : null;
      }
    },
    goToMapping() {
      this.step = 2;
    },
    buildMappingPayload() {
      const payload = [];
      for (const entry of this.mapping) {
        if (!entry.source_column) continue;
        const type = this.columnType(entry.column_id);
        const item = { column_id: entry.column_id, source_column: entry.source_column };
        if (type === 'number') {
          item.decimal_separator = entry.decimal_separator;
          item.thousands_separator = entry.thousands_separator;
        } else if (type === 'date') {
          item.date_format = entry.date_format;
        } else if (type === 'select') {
          item.recognize_boolean = entry.recognize_boolean;
        } else if (type === 'magoquery' || type === 'portalquery') {
          item.lookup_mode = entry.lookup_mode;
        }
        payload.push(item);
      }
      return payload;
    },
    async runValidation() {
      this.step = 3;
      this.validating = true;
      this.report = null;
      try {
        const { data } = await axios.post(
          route('modules.registri-personalizzati.import.validate', this.registry.id),
          {
            headers: this.headers,
            rows: this.rows,
            mapping: this.buildMappingPayload(),
          }
        );
        this.report = data;
      } catch (e) {
        this.uploadError = this.$t('rp.import.errors.http');
        this.step = 2;
      } finally {
        this.validating = false;
      }
    },
    async commit() {
      if (this.committing) return;
      this.committing = true;
      this.commitError = null;
      try {
        const { data } = await axios.post(
          route('modules.registri-personalizzati.import.commit', this.registry.id),
          {
            headers: this.headers,
            rows: this.rows,
            mapping: this.buildMappingPayload(),
          }
        );
        this.createdCount = data.created;
        this.committed = true;
      } catch (e) {
        const msg = e.response && e.response.data && e.response.data.message;
        this.commitError = msg || this.$t('rp.import.errors.http');
      } finally {
        this.committing = false;
      }
    },
  },
};
</script>
